import React, { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, Button, Spinner, Text, useColorModeValue, Divider, IconButton } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { ActualClassDetails, Lesson } from "../Common/types";
import {  Flex, Heading, Icon, Table, Tbody, Td, Th, Thead, Tr,
    Tooltip,
    Link,
    HStack, Stack } from "@chakra-ui/react";
import { FaBook, FaClock, FaMapMarkerAlt, FaUser, FaInfoCircle, FaEye } from "react-icons/fa";
import { format } from "date-fns";
import { InfoOutlineIcon, ViewIcon } from "@chakra-ui/icons";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const LessonSummaryViewer = () => {
    const { plannedClassId } = useParams<{ plannedClassId: string }>();
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfTitle, setPdfTitle] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [loadingLessonId, setLoadingLessonId] = useState<number | null>(null);
    const [contentLoadingLessonId, setContentLoadingLessonId] = useState<number | null>(null);

    const [summaryLessonId, setSummaryLessonId] = useState<number | null>(null);
    const [contentLessonId, setContentLessonId] = useState<number | null>(null);

    const [containerWidth, setContainerWidth] = useState<number>(window.innerWidth * 0.9); // Default to 90% of screen width

    const containerRef = useRef<HTMLDivElement | null>(null);

    const bg = useColorModeValue("gray.100", "gray.700");

    const dividerBg = useColorModeValue("gray.100", "gray.700");

    const location = useLocation();
    const selectedClass: ActualClassDetails | undefined = location.state?.selectedClass;
    const [expandedLesson, setExpandedLesson] = useState<number | null>(null);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth(); // Set initial width
        window.addEventListener("resize", updateWidth);

        return () => {
            window.removeEventListener("resize", updateWidth);
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl]);

    if (!selectedClass) {
        return <p>No class details available.</p>;
    }

   // const startTimeFormatted = format(new Date(selectedClass.start_time), "PPpp");

    const startTimeFormatted = fmtLocal(selectedClass.start_time, {
        weekday: "short",
        day:     "numeric",
        month:   "short",
        hour:    "numeric",
        minute:  "2-digit",
        hour12:  true,
    });

    console.log("start time formatted", startTimeFormatted)
    
    const endTimeFormatted = fmtLocal(selectedClass.end_time, {
        weekday: "short",
        day:     "numeric",
        month:   "short",
        hour:    "numeric",
        minute:  "2-digit",
        hour12:  true,
    });

//    const endTimeFormatted = format(new Date(selectedClass.end_time), "PPpp");
   // const isClassLive = new Date() >= new Date(selectedClass.start_time);

    const now = Date.now(); 
    
//    const nowMs = now.getTime();    

    const classStartTime = parseUtc(selectedClass.start_time).getTime();

//    const isClassLive = Date.now() >= parseUtc(selectedClass.start_time).getTime();

    const isClassLive = now >= classStartTime;

    console.log("Is the current class live",now, classStartTime);

    console.log("printing selected class start time",selectedClass.start_time,isClassLive );
  
    
    
    const handleLessonSummary = async (lesson: Lesson) => {
        setSummaryLessonId(lesson.id);
        setContentLessonId(null);
        setLoadingLessonId(lesson.id);
        setPdfTitle(`Summary of Lesson - ${lesson.title}`);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";

            const response = await fetch(`${apiUrl}/stream-summary-content/${lesson.id}/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            if (!response.ok) throw new Error("Failed to fetch summary PDF");

            const pdfBlob = await response.blob();
            setPdfUrl(URL.createObjectURL(pdfBlob));
        } catch (error) {
            console.error("Error fetching PDF:", error);
        } finally {
            setLoadingLessonId(null);
        }
    };

        const handleViewFullContent = async (lesson: Lesson) => {
            setSummaryLessonId(null);
            setContentLessonId(lesson.id);
            setContentLoadingLessonId(lesson.id);
            setPdfTitle(`Full Lesson - ${lesson.title}`);
            try {
                const apiUrl = process.env.REACT_APP_API_URL || "https://default-url.com";
    
                const response = await fetch(`${apiUrl}/stream-full-content/${lesson.id}/`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                if (!response.ok) throw new Error("Failed to fetch summary PDF");
    
                const pdfBlob = await response.blob();
                setPdfUrl(URL.createObjectURL(pdfBlob));
            } catch (error) {
                console.error("Error fetching PDF:", error);
            } finally {
                setContentLoadingLessonId(null);
            }
        };

    return (
        <Box width="100%" bg={bg} minH="100vh" p={2}>  {/* Full background */}
            <Box
                p={4}
                bg="white"
                borderRadius="lg"
                boxShadow="xl"
                borderWidth="1px"
                borderColor="gray.200"
                transition="0.3s"
                maxW="1200px"
                mx="auto"
                >
                {/* Class Title */}
                <Heading size="lg" mb={4} color="teal.600">
                    {selectedClass.batch_title}
                </Heading>

                                {/* Tutor & Course Info */}
                                <Flex mt={4} gap={4} flexWrap="wrap">
                    <Flex align="center" gap={2}>
                    <Icon as={FaUser} color="blue.500" />
                    <Text fontSize="sm">
                        <strong>Tutor:</strong> {selectedClass.tutor_name || "Not Assigned"}
                    </Text>
                    </Flex>
                    <Flex align="center" gap={2}>
                    <Icon as={FaMapMarkerAlt} color="red.500" />
                    <Text fontSize="sm">
                        <strong>Course:</strong> {selectedClass.course_title || "N/A"}
                    </Text>
                    </Flex>
                </Flex>

                {/* Class Schedule */}
                <Flex mt={4} gap={4} flexWrap="wrap">
                    <Flex align="center" gap={2}>
                    <Icon as={FaClock} color="green.500" />
                    <Text fontSize="sm">
                        <strong>Start:</strong> {startTimeFormatted}
                    </Text>
                    </Flex>
                    <Flex align="center" gap={2}>
                    <Icon as={FaClock} color="orange.500" />
                    <Text fontSize="sm">
                        <strong>End:</strong> {endTimeFormatted}
                    </Text>
                    </Flex>
                </Flex>


                {/* Class Details */}
                <Box mt={3} p={4} bg="gray.50" borderRadius="md">
                    <Heading size="md" mb={2} color="purple.600">
                    Class # {selectedClass.sequence_number}
                    </Heading>
                    <Text fontSize="sm">
                    <strong>Title:</strong> {selectedClass.title}
                    </Text>
                    <Text fontSize="sm">
                    <strong>Description:</strong> {selectedClass.description || "No description provided"}
                    </Text>
                </Box>



                {/* Lessons Section */}
                {selectedClass.lessons.length > 0 && (
                <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                <Heading size="md" mb={3} color="purple.600">
                    <Icon as={FaBook} color="purple.500" mr={2} />
                    Lessons
                </Heading>
                <Table variant="simple" size="sm">
                    <Tbody>
                    {selectedClass.lessons.map((lesson) => (
                        <Tr key={lesson.id}>
                        <Td fontWeight="medium">
                            <Tooltip label={lesson.description} hasArrow>
                            <Text textColor={"gray.600"}> {lesson.title} </Text>
                                    
                            </Tooltip>
                        </Td>
                        <Td>
                            <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
                            <Button 
                            colorScheme={summaryLessonId === lesson.id ? "blue" : "gray"}
                            variant={summaryLessonId === lesson.id ? "solid" : "outline"}
                            size="sm" 
                            onClick={() => handleLessonSummary(lesson)} 
                             isLoading={loadingLessonId === lesson.id}
                            >
                                Summary
                            </Button>
                            <Button
                                colorScheme={contentLessonId === lesson.id ? "green" : "gray"}
                                variant={contentLessonId === lesson.id ? "solid" : "outline"}
                                size="sm"
                                onClick={() => handleViewFullContent(lesson)}
                                isLoading={contentLoadingLessonId === lesson.id}
                                isDisabled={!isClassLive}
                            >
                            Content
                            </Button>
    {/*                                 <Tooltip label="View Summary" aria-label="View Summary">    
                                <IconButton icon={<FaInfoCircle />}  aria-label="View Summary" onClick={() => handleLessonSummary(lesson)} />
                            </Tooltip>
                            <Tooltip label="View Full Content" aria-label="View Full Content">
                                <IconButton icon={<FaEye />}  aria-label="View Full Content" onClick={() => handleViewFullContent(lesson)}  isDisabled={!isClassLive} />
                            </Tooltip> */}
                            </Stack>
                        </Td>
                        </Tr>
                    ))}
                    </Tbody>
                </Table>
                </Box>
                    )}

            </Box>
            {pdfUrl && (
            <Box mt={2} ref={containerRef} maxW="1200px" mx="auto" p={4} bg={bg} borderRadius="lg" boxShadow="md">
                    <Box >
                        <Text fontWeight="bold" mb={2}>{pdfTitle}</Text>
                        <Document 
                            file={pdfUrl} 
                            onLoadSuccess={({ numPages }) => setNumPages(numPages || 0)}
                        >
                            {Array.from(new Array(numPages ?? 0), (_, index) => (
                                <Box key={index} p={4}  bg={dividerBg} display="flex" justifyContent="center">
                                    <Page 
                                        pageNumber={index + 1} 
                                        width={containerWidth-32}  
                                        renderTextLayer={false}  
                                        renderAnnotationLayer={false}  
                                    />

                                </Box>
                            ))}
                        </Document>
                    </Box>
            </Box>
             )}
        </Box>
    );
    
};

export default LessonSummaryViewer;
